import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { 
  FileJson, 
  Check, 
  X, 
  AlertCircle, 
  BookOpen,
  Upload,
  Layers,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

const CATEGORIES = [
  "Advanced Certificate",
  "Certificate",
  "Certifications",
  "Short Course",
  "Specialist Courses"
];

export default function BulkCourseUploader({ onClose, onSuccess }) {
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // File state
  const [file, setFile] = useState(null);
  const [coursesData, setCoursesData] = useState([]);
  const [parseError, setParseError] = useState(null);
  
  // Settings
  const [settings, setSettings] = useState({
    category: 'Short Course',
    cpd_points: 0,
    price: 0
  });
  
  // Results
  const [uploadResult, setUploadResult] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.json')) {
      toast.error('Please upload a .json file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size exceeds 50MB limit');
      return;
    }
    
    setFile(selectedFile);
    setUploading(true);
    setParseError(null);
    
    try {
      const content = await readFileAsText(selectedFile);
      const parsed = JSON.parse(content);
      
      // Support both single object and array of objects
      let coursesArray;
      if (Array.isArray(parsed)) {
        coursesArray = parsed;
      } else if (typeof parsed === 'object' && parsed !== null) {
        // Single object - wrap in array
        coursesArray = [parsed];
      } else {
        throw new Error('JSON must be an object or array of course objects');
      }
      
      if (coursesArray.length === 0) {
        throw new Error('JSON array is empty');
      }
      
      // Check for required 'title' field
      const missingTitle = coursesArray.findIndex(item => !item.title);
      if (missingTitle !== -1) {
        throw new Error(`Row ${missingTitle + 1} is missing required 'title' field`);
      }
      
      setCoursesData(coursesArray);
      setCurrentStep(1);
      toast.success(`Found ${parsed.length} courses to import`);
      
    } catch (error) {
      console.error('Parse error:', error);
      setParseError(error.message || 'Invalid JSON file');
      toast.error('Failed to parse JSON file');
      setFile(null);
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/json': ['.json'] },
    multiple: false,
    disabled: uploading
  });

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleBulkUpload = async () => {
    setCreating(true);
    
    try {
      const response = await axios.post(`${API}/json/bulk-courses`, {
        content: JSON.stringify(coursesData),
        category: settings.category,
        default_cpd_points: parseInt(settings.cpd_points) || 0,
        default_price: parseFloat(settings.price) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUploadResult(response.data);
      setCurrentStep(2);
      toast.success(`Created ${response.data.courses_created} courses!`);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create courses';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setCoursesData([]);
    setParseError(null);
    setUploadResult(null);
    setCurrentStep(0);
  };

  const STEPS = [
    { id: 'upload', label: 'Upload JSON' },
    { id: 'preview', label: 'Preview & Settings' },
    { id: 'result', label: 'Results' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Layers className="h-6 w-6 text-navigate-gold" />
            <div>
              <h2 className="font-heading text-xl font-bold text-white">Bulk Course Import</h2>
              <p className="text-sm text-zinc-400">Create multiple courses from JSON file</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-zinc-800 bg-zinc-900/50">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-2">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${index < currentStep 
                      ? 'bg-green-500 text-white' 
                      : index === currentStep 
                        ? 'bg-navigate-gold text-black'
                        : 'bg-zinc-800 text-zinc-500'}
                  `}>
                    {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                  </div>
                  <span className={`text-sm hidden sm:inline ${
                    index <= currentStep ? 'text-white' : 'text-zinc-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-green-500' : 'bg-zinc-700'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {currentStep === 0 && (
            <div className="space-y-6">
              {parseError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                  <p className="text-red-400 text-sm">{parseError}</p>
                </div>
              )}
              
              <div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all
                  ${isDragActive 
                    ? 'border-navigate-gold bg-navigate-gold/10' 
                    : 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/30'}
                  ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                data-testid="bulk-json-dropzone"
              >
                <input {...getInputProps()} />
                
                {uploading ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 border-4 border-navigate-gold border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-white">Parsing courses...</p>
                  </div>
                ) : (
                  <>
                    <Layers className="h-16 w-16 text-zinc-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">
                      {isDragActive ? 'Drop your JSON file here' : 'Drag & drop your bulk courses JSON'}
                    </h3>
                    <p className="text-zinc-400 text-sm mb-4">
                      or click to browse
                    </p>
                    <p className="text-xs text-zinc-500">
                      Supported: .json files up to 50MB
                    </p>
                  </>
                )}
              </div>

              {/* Expected Format */}
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <h4 className="text-sm font-medium text-white mb-3">Expected JSON Format:</h4>
                <pre className="text-xs text-zinc-400 font-mono overflow-x-auto">
{`[
  {
    "title": "Introduction to Data Science",
    "content": "Course content here...",
    "error": null,
    "attempts": 0
  },
  {
    "title": "Machine Learning Basics",
    "content": "Another course content...",
    ...
  }
]`}
                </pre>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Preview Table */}
              <div>
                <h4 className="text-sm font-medium text-zinc-400 mb-3">
                  Preview ({coursesData.length} courses to import)
                </h4>
                <div className="border border-zinc-800 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-800/50">
                        <TableHead className="text-zinc-400 w-12">#</TableHead>
                        <TableHead className="text-zinc-300">Title</TableHead>
                        <TableHead className="text-zinc-300 w-48">Content Preview</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coursesData.slice(0, 20).map((course, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="text-zinc-500 font-mono text-xs">{idx + 1}</TableCell>
                          <TableCell className="text-white">{course.title}</TableCell>
                          <TableCell className="text-zinc-400 text-sm truncate max-w-[200px]">
                            {course.content?.substring(0, 50)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {coursesData.length > 20 && (
                  <p className="text-xs text-zinc-500 mt-2">
                    Showing first 20 of {coursesData.length} courses
                  </p>
                )}
              </div>

              {/* Default Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-400">Default Settings (Applied to all courses)</h4>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={settings.category} 
                      onValueChange={(v) => setSettings(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="bg-navigate-dark border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-navigate-surface border-zinc-800">
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>CPD Points</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings.cpd_points}
                      onChange={(e) => setSettings(prev => ({ ...prev, cpd_points: e.target.value }))}
                      className="bg-navigate-dark border-zinc-700"
                    />
                  </div>
                  
                  <div>
                    <Label>Price (ZAR)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.price}
                      onChange={(e) => setSettings(prev => ({ ...prev, price: e.target.value }))}
                      className="bg-navigate-dark border-zinc-700"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={creating}
                  className="btn-gold flex items-center gap-2 disabled:opacity-50"
                  data-testid="bulk-create-courses-btn"
                >
                  {creating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Create {coursesData.length} Courses
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && uploadResult && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="h-10 w-10 text-green-400" />
              </div>
              
              <h3 className="text-xl font-heading font-bold text-white mb-2">
                Bulk Import Complete!
              </h3>
              
              <p className="text-zinc-400 mb-6">
                Created <span className="text-navigate-gold font-bold">{uploadResult.courses_created}</span> courses
                {uploadResult.errors_count > 0 && (
                  <span className="text-red-400"> with {uploadResult.errors_count} errors</span>
                )}
              </p>
              
              {/* Success Summary */}
              {uploadResult.created_courses.length > 0 && (
                <div className="max-w-md mx-auto mb-6">
                  <h4 className="text-sm font-medium text-zinc-400 mb-3 text-left">Created Courses:</h4>
                  <div className="max-h-40 overflow-y-auto border border-zinc-800 rounded-lg">
                    {uploadResult.created_courses.slice(0, 10).map((course, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border-b border-zinc-800 last:border-0">
                        <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
                        <span className="text-sm text-white truncate">{course.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Errors */}
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="max-w-md mx-auto mb-6">
                  <h4 className="text-sm font-medium text-red-400 mb-3 text-left">Errors:</h4>
                  <div className="max-h-32 overflow-y-auto border border-red-500/30 rounded-lg bg-red-500/10">
                    {uploadResult.errors.map((err, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border-b border-red-500/20 last:border-0">
                        <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                        <span className="text-sm text-red-300">Row {err.row}: {err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-center gap-3">
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                >
                  Upload Another
                </button>
                <button
                  onClick={onClose}
                  className="btn-gold"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
